<script setup lang="ts">
import { Fragment, h } from 'vue'
import { Icon } from '@iconify/vue'
import Box from './Box.vue'
import ShiftHeaderRow from './ShiftHeaderRow.vue'
import type { RescheduleInfo } from './types'
import { AppointmentTableRow } from './types'
import ActionButtonsCell from './ActionButtonsCell.vue'
import LicensePlateCell from './LicensePlateCell.vue'
import PickupNumberCell from './PickupNumberCell.vue'
import CombinedStatusCell from './CombinedStatusCell.vue'
import LFDCell from './LFDCell.vue'
import TimeCell from './TimeCell.vue'
import ContainerNumberCell from './ContainerNumberCell.vue'
import CheckboxCell from './CheckboxCell.vue'
import DispatchBulkActionsToolbar from './DispatchBulkActionsToolbar.vue'
import TransactionActions from '~/components/dispatch_table/TransactionActions.vue'
import StickyHeaderVirtualTable from '~/components/display/virtual_scrolling/StickyHeaderVirtualTable.vue'
import Help from '~/components/display/Help.vue'
import NewFeaturePopover from '~/components/NewFeaturePopover.vue'
import type {
  CellRendererParams,
  ColumnDef,
} from '~/components/display/virtual_scrolling/types'

import type { ShiftGroup } from '~/models/groupedAppointments'
import { type AppointmentStatus } from '~/services/apiClient'
import { useAppointmentsRefreshRequestsStore } from '~~/stores/appointmentsRefreshRequests'
import { useRescheduling } from '~/compositions/mutations/useRescheduling'
import type { AppointmentWithContainerInfo } from '~/models/appointmentWithContainerInfo'
import IssueBadges from '~/components/dispatch_table/IssueBadges.vue'
import { useReturnRulesLatest } from '~/stores/returnRulesLatest'

const props = defineProps<{
  height: number
  minWidth: number
  appointmentShiftGroups: ShiftGroup[]
  selectedShift: ShiftGroup | undefined
}>()

const emit = defineEmits({
  firstVisibleShiftChanged: (shiftGroup: ShiftGroup) => true,
  selectionChanged: (selectedRows: Set<string>) => true,
})

const returnRulesStore = useReturnRulesLatest()
const boxElement = ref<HTMLElement | null>(null)
const virtualTableRef = ref<{ scrollToHeader: (headerID: string) => void }>()

// Selection state for dispatch table rows
const selectedRows = ref(new Set<string>())
const appointmentsRefreshRequestsStore = useAppointmentsRefreshRequestsStore()
const rescheduling = useRescheduling()
const hoveredRowKey = ref<string | null>(null)
const rowHeight = 38
const headerHeight = 32
const shiftHeaderHeight = 28
const shiftHeaderHeightPixels = `${shiftHeaderHeight}px`

const rows = computed((): AppointmentTableRow[] => {
  const rows: AppointmentTableRow[] = []

  for (const shiftGroup of props.appointmentShiftGroups) {
    if (shiftGroup.countTotal === 0) continue
    for (const trx of shiftGroup.transactions) {
      rows.push(new AppointmentTableRow(shiftGroup, trx))
    }
  }
  return rows
})

// Memoized selection state for better performance
const selectionState = computed(() => {
  const selected = new Set(selectedRows.value)
  const totalRows = rows.value.length
  const selectedCount = selected.size

  return {
    selected,
    totalRows,
    selectedCount,
    allSelected: totalRows > 0 && selectedCount === totalRows,
    someSelected: selectedCount > 0 && selectedCount < totalRows,
  }
})
function triggerRefreshAppointments() {
  appointmentsRefreshRequestsStore.triggerRefresh()
}
// set a timer to refresh appointments every 5 minutes
const refreshInterval = setInterval(triggerRefreshAppointments, 20 * 60 * 1000)
onUnmounted(() => {
  clearInterval(refreshInterval)
})

function scrollToShift(shiftGroup: ShiftGroup) {
  virtualTableRef.value?.scrollToHeader(shiftGroup.key)
}

const openRescheduleRowKey = ref<string | null>(null)
function toggleReschedulePopover(rowKey: string) {
  openRescheduleRowKey.value =
    openRescheduleRowKey.value === rowKey ? null : rowKey
}
function closeReschedulePopover() {
  openRescheduleRowKey.value = null
}

// Selection functions
function toggleRowSelection(transactionKey: string) {
  const newSelection = new Set(selectedRows.value)
  if (newSelection.has(transactionKey)) {
    newSelection.delete(transactionKey)
  } else {
    newSelection.add(transactionKey)
  }
  selectedRows.value = newSelection

  // Emit the selection change
  emit('selectionChanged', newSelection)
}

function clearSelection() {
  // Direct assignment is faster than calling clear() for large Sets
  selectedRows.value = new Set()

  // Emit the selection change
  emit('selectionChanged', new Set(selectedRows.value))
}

function selectAllRows() {
  // Build the Set of all transaction keys in one operation for better performance
  const allKeys = new Set(
    rows.value.map((row) => row.transaction.stable_transaction_key)
  )
  selectedRows.value = allKeys

  // Emit the selection change
  emit('selectionChanged', new Set(selectedRows.value))
}

// Memoized selected appointments - only recalculate when selection changes
const selectedAppointments = computed(() => {
  const { selected } = selectionState.value
  const appts: AppointmentWithContainerInfo[] = []

  for (const shiftGroup of props.appointmentShiftGroups) {
    for (const transaction of shiftGroup.transactions) {
      if (selected.has(transaction.stable_transaction_key)) {
        appts.push(...transaction.appointments)
      }
    }
  }

  return appts
})

// Memoized row selection check - use a Map for O(1) lookup
const rowSelectionMap = computed(() => {
  const map = new Map<string, boolean>()
  const { selected } = selectionState.value

  for (const row of rows.value) {
    map.set(
      row.transaction.stable_transaction_key,
      selected.has(row.transaction.stable_transaction_key)
    )
  }

  return map
})

function isRowSelected(transactionKey: string): boolean {
  return rowSelectionMap.value.get(transactionKey) ?? false
}

defineExpose({
  scrollToShift,
  clearSelection,
})
type CellParams = CellRendererParams<AppointmentTableRow>

const CONTAINER_COL_WIDTH = 160

const columns: ColumnDef<AppointmentTableRow>[] = [
  {
    minWidth: 25,
    maxWidth: 25,
    title: '',
    cellRenderer: ({ rowData }: CellParams) => {
      return h(CheckboxCell, {
        rowData,
        isSelected: isRowSelected(rowData.transaction.stable_transaction_key),
        onToggle: toggleRowSelection,
      })
    },
    headerRenderer: () => {
      const { allSelected, someSelected } = selectionState.value

      return h(
        NewFeaturePopover,
        {
          featureKey: 'bulk-select-enhancement',
          title: '☑️ Bulk Selection',
          message:
            'Click "Select all" or select individual rows to perform bulk actions',
          expirationDate: '2025-07-10',
          placement: 'bottom',
          width: 280,
          delay: 5000,
        },
        {
          default: () =>
            h('input', {
              type: 'checkbox',
              checked: allSelected,
              indeterminate: someSelected && !allSelected,
              onChange: (e: Event) => {
                const target = e.target as HTMLInputElement
                if (target.checked) {
                  selectAllRows()
                } else {
                  clearSelection()
                }
              },
              class: 'cursor-pointer',
            }),
        }
      )
    },
  },
  {
    minWidth: 150,
    maxWidth: 180,
    flexGrow: 1,
    title: 'TIME',
    cellRenderer: TimeCell,
    headerRenderer: () => {
      return h('div', [
        h('span', { class: 'text-gray-400 text-xs mr-2' }, 'Select All'),
        h('span', 'TIME'),
      ])
    },
  },
  {
    minWidth: 50,
    maxWidth: 65,
    title: 'LFD',
    cellRenderer: LFDCell,
  },
  {
    minWidth: 75,
    maxWidth: 75,
    flexGrow: 1,
    title: 'TERM',
    dataKey: 'terminalLabel',
  },
  {
    minWidth: CONTAINER_COL_WIDTH + 5,
    title: 'DROP-OFF',
    cellRenderer: ({ rowData }: CellParams) => {
      return h(ContainerNumberCell, {
        containerInfo: rowData.inboundContainerInfo,
        isDual: !!rowData.outboundContainerInfo,
        transaction: rowData.transaction,
        rowIsHovered: hoveredRowKey.value === rowData.rowKey,
      })
    },
  },
  {
    minWidth: CONTAINER_COL_WIDTH + 30,
    title: 'PICK-UP',
    cellRenderer: ({ rowData }: CellParams) => {
      return h(ContainerNumberCell, {
        containerInfo: rowData.outboundContainerInfo,
        isDual: false,
        transaction: rowData.transaction,
        rowIsHovered: hoveredRowKey.value === rowData.rowKey,
      })
    },
  },
  {
    minWidth: 120,
    title: 'STATUS',
    cellRenderer: ({ rowData }: CellParams) => {
      const containerInfo = rowData.transaction?.primary_appointment
      const moveType =
        rowData.transaction?.primary_appointment?.appointment.moveType
      const isDual = !!(
        rowData.transaction?.inbound_appointment &&
        rowData.transaction?.outbound_appointment
      )
      return h(CombinedStatusCell, {
        status: rowData.statusDescription as AppointmentStatus,
        containerInfo,
        moveType,
        appointment: rowData.transaction?.primary_appointment?.appointment,
        isDual,
        transaction: rowData.transaction,
      })
    },
    headerRenderer: () => {
      return h('div', { class: 'flex items-center gap-1' }, [
        h('span', 'STATUS'),
        h(
          Help,
          {
            title: 'Status & Move Type Icons',
            width: 350,
          },
          {
            default: () => [
              h(
                'p',
                'The status column shows both the appointment status and move type:'
              ),
              h('ul', [
                h('li', [
                  h(Icon, {
                    icon: 'tabler:arrow-big-right-filled',
                    class: 'inline mr-1',
                  }),
                  'Load out (import - filled right arrow)',
                ]),
                h('li', [
                  h(Icon, {
                    icon: 'tabler:arrow-big-left-filled',
                    class: 'inline mr-1',
                  }),
                  'Load in (export - filled left arrow)',
                ]),
                h('li', [
                  h(Icon, {
                    icon: 'tabler:arrow-big-right',
                    class: 'inline mr-1',
                  }),
                  'Empty out (empty right arrow)',
                ]),
                h('li', [
                  h(Icon, {
                    icon: 'tabler:arrow-big-left',
                    class: 'inline mr-1',
                  }),
                  'Empty in (empty left arrow)',
                ]),
                h('li', [
                  'Dual transaction will have both arrows (out on top, in on bottom)',
                ]),
              ]),
            ],
          }
        ),
      ])
    },
  },
  {
    minWidth: 86,
    maxWidth: 160,
    flexGrow: 1,
    title: 'PICKUP #',
    cellRenderer: PickupNumberCell,
  },
  {
    minWidth: 120,
    maxWidth: 300,
    flexGrow: 1,
    title: 'LICENSE PLATE',
    cellRenderer: ({ rowData }: CellParams) => {
      if (!rowData.transaction) {
        return h('div')
      }
      return h(LicensePlateCell, {
        transaction: rowData.transaction,
      })
    },
  },
  {
    minWidth: 140,
    title: '',
    cellRenderer: ({ rowData }: CellParams) => {
      const reschedulingInfo = getReschedulingInfoForRow(rowData)

      return h(ActionButtonsCell, {
        transaction: rowData.transaction,
        rescheduling: !!reschedulingInfo,
        reschedulingStatus: reschedulingInfo?.state.status,
        rowData,
        openRescheduleRowKey: openRescheduleRowKey.value,
        onToggleReschedulePopover: (rk: string) => toggleReschedulePopover(rk),
        onCloseReschedulePopover: closeReschedulePopover,
        onRescheduleBegun: rescheduling.registerReschedule,
      })
    },
    headerRenderer: () => {
      return h('div', [
        h(
          NewFeaturePopover,
          {
            featureKey: 'bulk-actions-enhancement',
            title: '⚡ Bulk Actions',
            message:
              'Perform actions on multiple selected containers! You can cancel, export and copy',
            expirationDate: '2025-07-10',
            placement: 'bottom',
            width: 300,
            delay: 6000,
          },
          {
            default: () =>
              h(DispatchBulkActionsToolbar, {
                selectedAppointments: selectedAppointments.value,
              }),
          }
        ),
      ])
    },
  },
  {
    minWidth: 120,
    flexGrow: 2,
    title: '',
    cellRenderer: ({ rowData }: CellParams) => {
      const allTags = rowData.transaction?.allTags || []
      return h(Fragment, null, [
        h(TransactionActions, { transaction: rowData.transaction }),
        h(IssueBadges, {
          badges: allTags,
          appointmentTransaction: rowData.transaction,
          returnRulesStore,
        }),
      ])
    },
  },
]

function getReschedulingInfoForRow(
  row: AppointmentTableRow
): RescheduleInfo | undefined {
  if (!row.transaction) return
  if (!row.transaction.primary_appointment) return
  return rescheduling.getRescheduleInfo(
    row.transaction.primary_appointment.appointment.id
  )
}

const minColsWidth = computed(() => {
  return props.minWidth
})

function getShiftGroupID(row: AppointmentTableRow): string {
  return row.shiftGroup.key
}

function getSubGroupID(row: AppointmentTableRow): string {
  return row.transaction.stable_transaction_key
}

// Convert height to string for style binding
const tableHeight = computed(() => `${props.height}px`)
// TODO: Re-work these things for new table

function onHeaderChange(headerid: string | null) {
  if (!headerid) return
  // The number of shifts will always be relatively small (like less than 20, normally
  // like 6) so it's fine this is O(n) I feel
  const shiftGroup = props.appointmentShiftGroups.find(
    (shiftGroup) => shiftGroup.key === headerid
  )
  if (shiftGroup) {
    emit('firstVisibleShiftChanged', shiftGroup)
  }
}

const extraRowProps = ({ rowData }: { rowData: AppointmentTableRow }) => {
  return {
    onMouseenter: () => {
      hoveredRowKey.value = rowData.rowKey
    },
    onMouseleave: () => {
      hoveredRowKey.value = null
    },
  }
}

const rowClass = ({ rowData }: { rowData: AppointmentTableRow }) => {
  const classes = []
  if (rowData.shiftGroup.key === props.selectedShift?.key) {
    classes.push('is-selected')
  }
  const reschedulingInfo = getReschedulingInfoForRow(rowData)
  if (reschedulingInfo?.state) {
    if (
      reschedulingInfo.state.status === 'pending' ||
      reschedulingInfo.state.status === 'booked'
    ) {
      classes.push('rescheduling')
    } else if (reschedulingInfo.state.status === 'failed') {
      classes.push('rescheduling-failed')
    } else if (reschedulingInfo.state.status === 'rejected') {
      classes.push('rescheduling-rejected')
    }
  }
  return classes
}

// Static table key - don't re-render entire table on selection changes
const tableKey = 'dispatch-table'
</script>

<template>
  <Box
    ref="boxElement"
    class="appointments-table"
    :style="{ minWidth: `${minColsWidth}px`, height: tableHeight }"
  >
    <StickyHeaderVirtualTable
      ref="virtualTableRef"
      :key="tableKey"
      :columns="columns"
      :items="rows"
      :row-height="rowHeight"
      :header-height="headerHeight"
      :group-header-height="shiftHeaderHeight"
      :sub-group-header-height="0"
      :get-group-header-id="getShiftGroupID"
      :get-sub-group-header-id="getSubGroupID"
      :row-class="rowClass"
      :row-props="extraRowProps"
      :buffer-pixels="2000"
      @header-change="onHeaderChange"
    >
      <template #group-header="{ item }">
        <ShiftHeaderRow
          :shift-group="item.shiftGroup"
          class="shift-header"
          :class="{
            'is-selected': item.shiftGroup.key === selectedShift?.key,
          }"
        />
      </template>
    </StickyHeaderVirtualTable>
  </Box>
</template>

<style lang="scss" scoped>
.appointments-table {
  @apply text-sm h-full;
  $shift-header-height: v-bind(shiftHeaderHeightPixels);

  :deep(.sticky-header-virtual-table) {
    width: 100%;
  }

  .shift-header {
    height: $shift-header-height !important;
    @apply bg-blue-gray-300 px-2 border-b border-t border-blue-gray-400;

    // This fixes header lower border, otherwise it gets hidden
    position: relative;
    z-index: 20;
    margin-bottom: -1px !important;
    &.is-selected {
      // TODO: Use common vars with ShiftOption.vue
      @apply bg-blue-100 rounded-4px;
      @apply border-blue-700 bg-blue-100 !important;
      border-width: 2px;
    }
  }
  .rescheduling {
    @apply bg-yellow-100;
  }
  .rescheduling-failed {
    @apply bg-red-100;
  }
  .rescheduling-rejected {
    @apply bg-orange-100;
  }
}
</style>
